import { useEffect, useRef, useCallback } from 'react';

export interface ScanConfig {
  /** Minimum barcode length to accept (default: 18 for SSCC) */
  minLength?: number;
  /** Maximum gap between keystrokes in ms before buffer resets (default: 80) */
  maxGap?: number;
  /** Callback fired when a valid barcode scan is detected */
  onScan: (code: string) => void;
}

/**
 * Custom React hook for detecting HID barcode scanner input.
 *
 * Barcode scanners appear as keyboard devices (HID) and emit rapid keydown
 * events followed by Enter. This hook distinguishes scanner input from manual
 * typing by measuring the gap between keystrokes: scanner input arrives in
 * bursts (< 80ms between keys), while human typing is slower.
 *
 * Uses document-level keydown listener (not input-focused) to avoid iOS
 * keyboard suppression issues with Bluetooth HID scanners on iPad.
 */
export function useBarcodeScanner(config: ScanConfig): void {
  const { minLength = 18, maxGap = 80, onScan } = config;

  const bufferRef = useRef<string>('');
  const lastKeystrokeRef = useRef<number>(0);
  const onScanRef = useRef(onScan);

  // Keep onScan ref current to avoid stale closure issues
  onScanRef.current = onScan;

  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      const now = Date.now();
      const gap = now - lastKeystrokeRef.current;

      if (event.key === 'Enter') {
        if (bufferRef.current.length >= minLength) {
          event.preventDefault();
          onScanRef.current(bufferRef.current);
        }
        bufferRef.current = '';
        lastKeystrokeRef.current = 0;
        return;
      }

      // Only accumulate single printable characters
      if (event.key.length !== 1) {
        return;
      }

      // Reset buffer if gap exceeds threshold (manual typing)
      if (lastKeystrokeRef.current > 0 && gap > maxGap) {
        bufferRef.current = '';
      }

      bufferRef.current += event.key;
      lastKeystrokeRef.current = now;
    },
    [minLength, maxGap]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown]);
}
